import type { Doc } from "building-types/doc";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { Group } from "building-types/groups/group";
import type { GroupableEntityHandle, GroupHandle } from "building-types/handles/docEntityHandleTypes";
import type { RWTable } from "common/data/table";
import { maxSortIndexFromTable } from "common/utils/sortIndex";
import { docUpdateGroupState } from "../docUpdateGroupState";
import { docEntityHandleHash } from "./core/handleHash";
import { docGetEntityHandleByHash } from "./docEntityHandleHash";
import { docEntityTypeToDocTableName } from "./docEntityTypeToDocTableName";
import docGetEntity from "./docGetEntity";
import { docGetEntityHandleById } from "./docGetEntityHandleById";
import { entitySetNew } from "./entitySet";

export function docAddEntitiesToGroup(
  doc: Doc,
  handles: GroupableEntityHandle | GroupableEntityHandle[],
  groupHandle: GroupHandle,
): Doc {
  handles = Array.isArray(handles) ? handles : [handles];

  if (!handles.length) {
    return doc;
  }

  const targetGroup = doc.groups[groupHandle.id];
  if (!targetGroup) {
    return doc;
  }

  // Built-in/system catalog groups are read-only; new entities must never be inserted into them.
  if (targetGroup.isBuiltIn && !targetGroup.isDeletable) {
    return doc;
  }

  const entityType = handles[0].type;
  const docField = docEntityTypeToDocTableName[entityType];

  if (!docField) {
    return doc;
  }

  const table = doc[docField] as RWTable<DocEntityBase<typeof entityType>>;
  const updatedEntities: typeof table = {};
  const updatedGroups: RWTable<Group> = {};

  const indexOfGroup = targetGroup.sortIndex;

  const currentGroupEntities: typeof table = {};

  for (const hash in doc.entityGroups) {
    const entityGroupId = doc.entityGroups[hash];

    if (entityGroupId !== groupHandle.id) {
      continue;
    }

    const handle = docGetEntityHandleByHash(doc, hash);
    if (!handle) {
      continue;
    }
    const entity = docGetEntity(doc, handle) as DocEntityBase<typeof entityType>;
    currentGroupEntities[entity.id] = entity;
  }

  let maxSortIndexOfGroup = maxSortIndexFromTable(currentGroupEntities) + 1;
  const selectedEntityCount = handles.length;
  const selectedEntities = entitySetNew(handles);

  // iterate over table and increment all entities (groups included) that are below the group where the new entities were added
  for (const id in table) {
    const entity = table[id];
    const handle = docGetEntityHandleById(doc, id, [entity.entityType]);

    if (!handle) {
      continue;
    }

    const handleHash = docEntityHandleHash(handle);

    if (selectedEntities[handleHash]) {
      updatedEntities[id] = {
        ...entity,
        sortIndex: (maxSortIndexOfGroup += 1),
      };
    } else if (entity.sortIndex > indexOfGroup && !currentGroupEntities[id]) {
      updatedEntities[id] = {
        ...entity,
        sortIndex: entity.sortIndex + selectedEntityCount,
      };
    } else {
      updatedEntities[id] = entity;
    }
  }

  for (const id in doc.groups) {
    const group = doc.groups[id];

    if (group.sortIndex > indexOfGroup) {
      updatedGroups[id] = {
        ...group,
        sortIndex: group.sortIndex + selectedEntityCount,
      };
    } else {
      updatedGroups[id] = group;
    }
  }

  const entityGroup: RWTable<string> = {};

  for (const hash in selectedEntities) {
    entityGroup[hash] = groupHandle.id;
  }

  doc = {
    ...doc,
    entityGroups: {
      ...doc.entityGroups,
      ...entityGroup,
    },
    groups: updatedGroups,
    [docField]: updatedEntities,
  };
  return docUpdateGroupState(doc, groupHandle);
}
